import { useState, useEffect } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Skeleton,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { useNotificationPrefs, useUpdateNotificationPrefs } from '../../hooks/useAccount';

export default function NotificationPrefsPage() {
  const { data: prefs, isLoading, isError, error } = useNotificationPrefs();
  const updatePrefs = useUpdateNotificationPrefs();

  const [emailOnRsvp, setEmailOnRsvp] = useState(true);
  const [emailOnReminder, setEmailOnReminder] = useState(true);
  const [emailOnBilling, setEmailOnBilling] = useState(true);
  const [emailDigest, setEmailDigest] = useState('instant');
  const [success, setSuccess] = useState('');
  const [saveError, setSaveError] = useState('');

  useEffect(() => {
    if (prefs) {
      setEmailOnRsvp(prefs.emailOnRsvp);
      setEmailOnReminder(prefs.emailOnReminder);
      setEmailOnBilling(prefs.emailOnBilling);
      setEmailDigest(prefs.emailDigest);
    }
  }, [prefs]);

  const handleSave = async () => {
    setSuccess('');
    setSaveError('');

    try {
      await updatePrefs.mutateAsync({
        emailOnRsvp,
        emailOnReminder,
        emailOnBilling,
        emailDigest,
      });
      setSuccess('Notification preferences saved');
    } catch (err) {
      setSaveError((err as Error).message || 'Failed to save preferences');
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="50%" height={48} />
        <Skeleton variant="rectangular" height={300} sx={{ mt: 2, borderRadius: 2 }} />
      </Box>
    );
  }

  if (isError) {
    return (
      <Alert severity="error">
        {(error as Error)?.message ?? 'Failed to load notification preferences'}
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} mb={3}>
        <NotificationsIcon color="primary" sx={{ fontSize: 32 }} />
        <Typography variant="h4" fontWeight={700}>
          Notification Preferences
        </Typography>
      </Stack>

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      )}
      {saveError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError('')}>
          {saveError}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={600} mb={2}>
            Email Notifications
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            Choose which email notifications you would like to receive.
          </Typography>

          <Stack spacing={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={emailOnRsvp}
                  onChange={(e) => setEmailOnRsvp(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">RSVP Notifications</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get notified when a guest responds to your event invitation
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={emailOnReminder}
                  onChange={(e) => setEmailOnReminder(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Event Reminders</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Receive reminders about upcoming events you are hosting
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={emailOnBilling}
                  onChange={(e) => setEmailOnBilling(e.target.checked)}
                />
              }
              label={
                <Box>
                  <Typography variant="body1">Billing Notifications</Typography>
                  <Typography variant="body2" color="text.secondary">
                    Get notified about charges, receipts, and payment status changes
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', ml: 0 }}
            />
          </Stack>
        </CardContent>
      </Card>

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <FormControl component="fieldset">
            <FormLabel component="legend" sx={{ mb: 1 }}>
              <Typography variant="h6" fontWeight={600}>
                Email Digest Frequency
              </Typography>
            </FormLabel>
            <Typography variant="body2" color="text.secondary" mb={2}>
              How often would you like to receive email digest summaries?
            </Typography>

            <RadioGroup
              value={emailDigest}
              onChange={(e) => setEmailDigest(e.target.value)}
            >
              <FormControlLabel
                value="instant"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Instant</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Receive each notification as it happens
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                value="daily"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Daily Digest</Typography>
                    <Typography variant="body2" color="text.secondary">
                      One email per day summarizing all activity
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                value="weekly"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Weekly Digest</Typography>
                    <Typography variant="body2" color="text.secondary">
                      One email per week summarizing all activity
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start', mb: 1 }}
              />
              <FormControlLabel
                value="off"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body1">Off</Typography>
                    <Typography variant="body2" color="text.secondary">
                      Do not receive any digest emails
                    </Typography>
                  </Box>
                }
                sx={{ alignItems: 'flex-start' }}
              />
            </RadioGroup>
          </FormControl>
        </CardContent>
      </Card>

      <Button
        variant="contained"
        size="large"
        onClick={handleSave}
        disabled={updatePrefs.isPending}
        sx={{ minHeight: 48 }}
      >
        {updatePrefs.isPending ? 'Saving...' : 'Save Preferences'}
      </Button>
    </Box>
  );
}
